import React from "react";
import { Target, Eye, ShieldCheck, Award, Users, Clock } from "lucide-react";
import { PageHero } from "@/components/shared/PageHero";
import { SectionHeading } from "@/components/shared/SectionHeading";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/shared/Reveal";
import { Stats } from "@/components/home/Stats";
import { CTAButton } from "@/components/shared/CTAButton";
import { COMPANY, IMAGES } from "@/data/site";

const trust = [
  { icon: Award, title: "21+ Years of Expertise", desc: "Two decades of refining every step of the relocation journey." },
  { icon: ShieldCheck, title: "Safety First, Always", desc: "Insured transit and rigorous packing standards on every move." },
  { icon: Users, title: "200,000+ Happy Clients", desc: "A reputation built move by move, family by family." },
  { icon: Clock, title: "100% On-Time Record", desc: "Punctual pickups and deliveries you can plan around." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero
        breadcrumb="About Us"
        eyebrow="Our Journey"
        title="Moving India forward since 1999."
        description="What began as a single-city operation has grown into one of the region's most trusted names in relocation and logistics."
        image={IMAGES.cargoShip}
      />

      {/* Story */}
      <section className="bg-white py-24 lg:py-32">
        <div className="container-x grid lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-6 rounded-3xl overflow-hidden shadow-card ring-1 ring-brand/10">
            <img src={IMAGES.warehouse} alt="Our operations" className="w-full h-[440px] object-cover" loading="lazy" />
          </Reveal>
          <div className="lg:col-span-6">
            <SectionHeading eyebrow="Our Story" title="A legacy of secure, reliable moving." />
            <div className="mt-6 space-y-5 text-muted-foreground leading-relaxed">
              <p>
                Founded in {COMPANY.established}, {COMPANY.name} started with a simple promise — to treat every customer's belongings as if they were our own. Over {COMPANY.experience.toLowerCase()}, that promise has scaled into a full-service logistics network spanning local, state, pan-India and international moves.
              </p>
              <p>
                From our base in Gurgaon, we now operate a modern, GPS-enabled fleet, secure warehouses and a trained workforce that handles everything from a studio apartment to complex corporate relocations and cross-border freight.
              </p>
              <p>
                Technology, transparency and genuine care remain at the heart of everything we do — because moving is never just about goods, it's about people and their lives.
              </p>
            </div>
            <CTAButton to="/#quote" className="mt-8">Start Your Move</CTAButton>
          </div>
        </div>
      </section>

      {/* Mission / Vision */}
      <section className="bg-surface py-24 lg:py-32">
        <div className="container-x grid md:grid-cols-2 gap-6">
          <Reveal className="rounded-3xl bg-brand text-white p-10 shadow-card relative overflow-hidden">
            <div className="grain absolute inset-0" />
            <Target className="h-12 w-12 text-brand-sky relative" />
            <h3 className="relative mt-6 font-heading font-bold text-3xl">Our Mission</h3>
            <p className="relative mt-4 text-white/75 leading-relaxed">
              To deliver stress-free, secure and affordable relocation experiences through professional care, transparent pricing and dependable technology — every single time.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="rounded-3xl bg-white border border-gray-100 p-10 shadow-card">
            <Eye className="h-12 w-12 text-brand-accent" />
            <h3 className="mt-6 font-heading font-bold text-3xl text-ink">Our Vision</h3>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              To be India's most trusted logistics and moving partner — setting the benchmark for safety, punctuality and customer delight across every mile we travel.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Why trust us */}
      <section className="bg-white py-24 lg:py-32">
        <div className="container-x">
          <SectionHeading align="center" className="mb-16" eyebrow="Why customers trust us" title="Reliability you can measure." />
          <StaggerGroup className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {trust.map(({ icon: Icon, title, desc }) => (
              <StaggerItem key={title} className="h-full">
                <div className="h-full rounded-3xl border border-gray-100 bg-surface p-8 text-center hover:shadow-card transition-shadow duration-300" data-testid={`trust-${title.split(" ")[0].toLowerCase()}`}>
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-light text-brand">
                    <Icon className="h-7 w-7" />
                  </div>
                  <h4 className="mt-5 font-heading font-bold text-lg text-ink">{title}</h4>
                  <p className="mt-2 text-sm text-muted-foreground">{desc}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      <Stats />
    </div>
  );
}
